import React from 'react';
import { Icon } from '@iconify/react';
import { cn } from '../lib/utils';
import { TitleGrid } from './title-grid';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const SORT_OPTIONS = [
  { id: 'latest', label: 'Recently Added' },
  { id: 'episodes', label: 'Most Episodes' },
  { id: 'likes', label: 'Most Popular' },
  { id: 'alphabetical', label: 'Title (A–Z)' },
];

/**
 * Interactive filterable, sortable series gallery view.
 */
export function SeriesView({ series = [], genres = [] }) {
  const [selectedGenre, setSelectedGenre] = React.useState('all');
  const [sortBy, setSortBy] = React.useState('latest');
  const [searchQuery, setSearchQuery] = React.useState('');

  const filtered = React.useMemo(() => {
    let list = [...series];

    if (selectedGenre !== 'all') {
      list = list.filter((s) => (s.category || '').toLowerCase() === selectedGenre.toLowerCase());
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(
        (s) =>
          s.title.toLowerCase().includes(q) ||
          s.description?.toLowerCase().includes(q) ||
          s.director?.toLowerCase().includes(q)
      );
    }

    switch (sortBy) {
      case 'episodes':
        list.sort((a, b) => (b.episodeCount || 0) - (a.episodeCount || 0));
        break;
      case 'likes':
        list.sort((a, b) => (b.likes || 0) - (a.likes || 0));
        break;
      case 'alphabetical':
        list.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'latest':
      default:
        break;
    }

    return list;
  }, [series, selectedGenre, sortBy, searchQuery]);

  const activeSortLabel = SORT_OPTIONS.find((opt) => opt.id === sortBy)?.label || 'Sort';

  return (
    <div className="max-w-full gutter pt-8 pb-20 sm:pt-12 sm:pb-28">
      <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between border-b border-white/10 pb-6">
        <div>
          <h2 className="display text-2xl sm:text-3xl text-foreground">Browse All Series</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {filtered.length} {filtered.length === 1 ? 'series' : 'series'} available to binge
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2.5 sm:gap-3">
          <div className="relative flex-1 sm:w-56 sm:flex-none">
            <Icon
              icon="ph:magnifying-glass"
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Filter series…"
              className="h-9 w-full rounded-full border border-white/15 bg-white/5 pl-9 pr-8 text-xs text-foreground placeholder:text-muted-foreground outline-none transition focus:border-white/30 focus:bg-white/10"
            />
            {searchQuery ? (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                aria-label="Clear filter"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <Icon icon="ph:x-circle-fill" className="size-4" />
              </button>
            ) : null}
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex h-9 items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3.5 text-xs font-medium text-foreground transition hover:border-white/30 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Icon icon="ph:sort-ascending-bold" className="size-3.5 text-muted-foreground" />
                <span>{activeSortLabel}</span>
                <Icon icon="ph:caret-down-bold" className="size-3 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              {SORT_OPTIONS.map((opt) => (
                <DropdownMenuItem
                  key={opt.id}
                  onClick={() => setSortBy(opt.id)}
                  className={cn('cursor-pointer text-xs', sortBy === opt.id && 'font-semibold text-primary')}
                >
                  {opt.label}
                  {sortBy === opt.id ? <Icon icon="ph:check-bold" className="ml-auto size-3.5 text-primary" /> : null}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {genres.length > 0 ? (
        <div
          role="group"
          aria-label="Filter series by genre"
          className="mt-6 flex items-center gap-2 overflow-x-auto no-scrollbar pb-2"
        >
          <GenrePill active={selectedGenre === 'all'} count={series.length} onSelect={() => setSelectedGenre('all')}>
            All Genres
          </GenrePill>

          {genres.map((genre) => (
            <GenrePill
              key={genre.id}
              active={selectedGenre.toLowerCase() === genre.name.toLowerCase()}
              count={genre.count}
              onSelect={() => setSelectedGenre(genre.name)}
            >
              {genre.name}
            </GenrePill>
          ))}
        </div>
      ) : null}

      <div className="mt-8">
        {filtered.length > 0 ? (
          <TitleGrid titles={filtered} />
        ) : (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-white/8 bg-white/2 py-20 text-center">
            <div className="flex size-14 items-center justify-center rounded-full bg-white/5 text-muted-foreground ring-1 ring-white/10">
              <Icon icon="ph:television" className="size-7" />
            </div>
            <h3 className="display-sm mt-5 text-lg sm:text-xl text-foreground">No series found</h3>
            <p className="mt-2 max-w-[42ch] text-sm text-muted-foreground">
              {searchQuery
                ? `No series match "${searchQuery}". Try adjusting your keywords.`
                : 'There are currently no series filed under this genre.'}
            </p>
            <button
              type="button"
              onClick={() => {
                setSelectedGenre('all');
                setSearchQuery('');
              }}
              className="mt-6 inline-flex h-10 items-center rounded-full border border-white/20 bg-white/10 px-5 text-xs font-semibold text-white transition hover:bg-white/20"
            >
              Reset Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function GenrePill({ active, count, onSelect, children }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={active}
      className={cn(
        'flex shrink-0 h-9 items-center gap-2 rounded-full px-4 text-xs sm:text-sm font-medium transition-all duration-200',
        'focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
        active
          ? 'bg-white text-black font-semibold shadow-[0_2px_12px_rgba(255,255,255,0.2)]'
          : 'border border-white/10 bg-white/3 text-muted-foreground hover:border-white/25 hover:bg-white/8 hover:text-foreground'
      )}
    >
      <span>{children}</span>
      <span
        className={cn(
          'rounded-full px-1.5 py-0.5 text-[10px] font-semibold',
          active ? 'bg-black/15 text-black' : 'bg-white/10 text-muted-foreground'
        )}
      >
        {count}
      </span>
    </button>
  );
}

export default SeriesView;
